/**
 * ADR-128: calendar-aware durations — front-end mirror of
 * radar-api/src/utils/calendarDuration.js (keep the two in sync) ("X years, Y months, Z days").
 *
 * Rules:
 *  - Inclusive: the start date is day 1. [start, end] is measured as the
 *    calendar difference from `start` to `end + 1 day`, so start === end is
 *    1 day and "N years" is completed on the day before the Nth anniversary.
 *  - Month-end clamp: adding months clamps to the last day of a shorter
 *    month (31 Jan + 1 month = 28/29 Feb; 29 Feb + 1 year = 28 Feb), the
 *    same convention as date-fns `addMonths` (and ADR-093's tenure threshold).
 *  - Months are always counted from the original start date, never from a
 *    clamped intermediate, so 0 <= months <= 11 and
 *    start + (years, months) + days === end + 1 day.
 *  - Time-zone independent: every value is reduced to a calendar date
 *    (year, month, day) and all arithmetic is pure integer civil-calendar
 *    arithmetic (no Date statics), so it is also immune to tests that mock
 *    the global Date.
 *
 * Calendar-date reduction (`toCalendarDate`):
 *  - 'YYYY-MM-DD…' strings → that date (the time part is ignored).
 *  - Date at exactly UTC midnight → its UTC date. This is how Prisma returns
 *    `@db.Date` columns (hire_date, resign_date, start_date, end_date).
 *  - Any other Date (e.g. `new Date()`) → its local date, the ADR-017
 *    business-date convention for "today".
 */

export const ZERO_DURATION = Object.freeze({ years: 0, months: 0, days: 0 });
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * @param {Date|string|number|null|undefined} value
 * @returns {{year:number, month:number, day:number}|null} month is 0-based
 */
export function toCalendarDate(value) {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    const match = ISO_DATE_PREFIX.exec(value);
    if (match) {
      return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
    }
  }

  // Duck-typed so a Date instance is read as-is even when the global Date is mocked
  const date = value && typeof value.getTime === 'function' ? value : new Date(value);
  if (!date || typeof date.getTime !== 'function' || Number.isNaN(date.getTime())) return null;

  const isUtcMidnight = date.getUTCHours() === 0
    && date.getUTCMinutes() === 0
    && date.getUTCSeconds() === 0
    && date.getUTCMilliseconds() === 0;

  return isUtcMidnight
    ? { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() }
    : { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
}

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const daysInMonth = (year, month) => (month === 1 && isLeapYear(year) ? 29 : MONTH_LENGTHS[month]);

/**
 * Days since 1970-01-01 for a proleptic Gregorian date (month 0-based).
 * Integer "days from civil" algorithm (H. Hinnant).
 */
function toEpochDay({ year, month, day }) {
  const m = month + 1;
  const y = m <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

/** Inverse of toEpochDay. */
function fromEpochDay(epochDay) {
  const z = epochDay + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return { year: yoe + era * 400 + (m <= 2 ? 1 : 0), month: m - 1, day };
}

/** Epoch day of `start` + `totalMonths`, clamped to the target month's last day. */
function addMonthsClampedEpochDay(start, totalMonths) {
  const monthIndex = start.month + totalMonths;
  const year = start.year + Math.floor(monthIndex / 12);
  const month = ((monthIndex % 12) + 12) % 12;
  const day = Math.min(start.day, daysInMonth(year, month));
  return toEpochDay({ year, month, day });
}

/**
 * Inclusive calendar duration between two dates.
 * @param {Date|string|null} startDate
 * @param {Date|string|null} endDate
 * @returns {{years:number, months:number, days:number}} zeros when either
 *   date is missing or end is before start
 */
export function calculateCalendarDuration(startDate, endDate) {
  const start = toCalendarDate(startDate);
  const end = toCalendarDate(endDate);
  if (!start || !end) return { ...ZERO_DURATION };

  const startDay = toEpochDay(start);
  const endExclusiveDay = toEpochDay(end) + 1;
  if (endExclusiveDay <= startDay) return { ...ZERO_DURATION };

  // Largest whole number of months that fits, counted from the original start.
  const endExclusive = fromEpochDay(endExclusiveDay);
  let totalMonths = (endExclusive.year - start.year) * 12 + (endExclusive.month - start.month);
  while (totalMonths > 0 && addMonthsClampedEpochDay(start, totalMonths) > endExclusiveDay) {
    totalMonths -= 1;
  }

  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
    days: endExclusiveDay - addMonthsClampedEpochDay(start, totalMonths),
  };
}

/**
 * Inclusive number of calendar days in [startDate, endDate].
 * @returns {number} 0 when either date is missing or end is before start
 */
export function countCalendarDaysInclusive(startDate, endDate) {
  const start = toCalendarDate(startDate);
  const end = toCalendarDate(endDate);
  if (!start || !end) return 0;
  return Math.max(0, toEpochDay(end) - toEpochDay(start) + 1);
}

/**
 * ADR-196 (mirror of radar-api): the end of an ELAPSED interval — the earlier
 * of a (possibly future) end date and "today", compared as calendar dates. A
 * duration that answers "how long so far" must never count days that have
 * not happened yet. A missing or unreadable end falls back to today.
 *
 * Returns one of the two inputs unchanged, so callers keep the same
 * calendar-date reduction (`toCalendarDate`) downstream.
 *
 * @param {Date|string|null|undefined} endDate - scheduled or actual end
 * @param {Date|string} today - reference date for "today"
 * @returns {Date|string}
 */
export function elapsedEnd(endDate, today) {
  const end = toCalendarDate(endDate);
  if (!end) {
    return today;
  }
  const now = toCalendarDate(today);
  if (!now) {
    return endDate;
  }
  return toEpochDay(end) <= toEpochDay(now) ? endDate : today;
}
